import React, { useLayoutEffect } from "react"

import "./styles.scss"
import InsightsHeader from "./InsightsHeader"
import insightsBackground from "../images/insightsBackground.webp"
import { Helmet } from "react-helmet"
import { latestYear } from "../DataLoader"

// 2026 insight graphics, one per section (Figma: Insights-2026 / Insights-Desktop).
// Exported at 2x so they stay sharp on retina screens; display size is set below.
import graphGlobalDecline from "../images/insights-2026-global-decline.webp"
import graphCivicSpace from "../images/insights-2026-civic-space.webp"
import graphGovernmentInterference from "../images/insights-2026-government-interference.webp"
import graphAuthoritarianEntrenchment from "../images/insights-2026-authoritarian-entrenchment.webp"
import graphAccessToJustice from "../images/insights-2026-access-to-justice.webp"
import graphPricesStable from "../images/insights-2026-prices-stable.webp"
import graphRiskLower from "../images/insights-2026-risk-lower.webp"

function Insight(props) {
  const { url, text, header, subheader, image, alt, width, height } = props

  return (
    <div className="Insight">
      <span className="navLink" id={url.replace("#", "")} />
      {text && <div className="InsightTitle">{text}</div>}
      <div className="InsightContent">
        <div className="insightHeader">{header}</div>
        {subheader && <div className="insightSubheader">{subheader}</div>}
        <img src={image} alt={alt} width={width} height={height} />
      </div>
    </div>
  )
}

// width/height are the display size from the Figma layout. For a section that
// continues the one before it, set showInNav to false and leave out text so it
// has no section label.
const insights = [
  {
    text: "Global decline in rule of law",
    url: "#global-decline",
    header: "Rule of law erosion is slowing, but the recession persists.",
    subheader:
      "While fewer countries declined in 2026 than last year, the global balance trends negative.",
    image: graphGlobalDecline,
    width: 549,
    height: 566,
    alt: "62% of countries have declined.",
  },
  {
    text: "Rollbacks in civic space",
    url: "#rollbacks-in-civic-space",
    header: "Avenues for civic space are narrowing.",
    subheader:
      "Civic participation and the freedoms of speech and assembly each fell in over 60% of countries.",
    image: graphCivicSpace,
    width: 598,
    height: 512,
    alt: "Percentage of countries that declined in 2026: 65% civic participation, 64% freedom of speech, 67% freedom of assembly.",
  },
  {
    text: "Government interference",
    url: "#government-interference",
    header: "Government interference is compromising the justice system.",
    subheader:
      "Improper government influence has undermined civil and criminal justice in most countries.",
    image: graphGovernmentInterference,
    width: 650,
    height: 484,
    alt: "Percentage of countries that declined in 2026: 63% government powers limited by judiciary, 64% improper government influence in civil justice, 58% improper government influence in criminal justice.",
  },
  {
    text: "Authoritarian entrenchment",
    url: "#authoritarian-entrenchment",
    header: (
      <>
        Authoritarianism is no longer an emerging trend
        <br />
        —it is entrenched.
      </>
    ),
    image: graphAuthoritarianEntrenchment,
    width: 612,
    height: 580,
    alt: "86% of countries have suffered a sustained five-year decline in at least one of the mechanisms that keep authoritarianism in check: constraints on government powers, open government, and fundamental rights.",
  },
  {
    text: "Progress in accessibility",
    url: "#progress-in-accessibility",
    header: "Improvements in access to justice are emerging.",
    subheader:
      "Civil justice is becoming more accessible, affordable, and equal.",
    image: graphAccessToJustice,
    width: 568,
    height: 545,
    alt: "Percentage of countries that improved in 2026: 54% people can access and afford civil justice, 52% civil justice is free of discrimination.",
  },
  {
    text: "Economic prosperity",
    url: "#economic-prosperity",
    header: "Where the rule of law is stronger, prices are stable",
    subheader:
      "Strong rule of law environments correlate with contained inflation, while weaker governance structures consistently coincide with higher price volatility and severe inflationary spikes.",
    image: graphPricesStable,
    width: 525,
    height: 525,
    alt: "Above a score of 0.60, countries tend to unlock stable prices with below-median rates of inflation. Below 0.60, inflation rates are higher than the 3.23% median.",
  },
  {
    url: "#economic-prosperity-risk",
    showInNav: false,
    header: "and risk is lower.",
    subheader:
      "When Index scores reach 0.66, countries tend to have high Sovereign Credit Ratings; below that score, countries tend to have speculative grade borrowing penalties.",
    image: graphRiskLower,
    width: 525,
    height: 429,
    alt: "At a score of 0.66 and above, countries tend to have a good investment grade credit rating (AAA to BBB). Below 0.66, countries tend to have a speculative grade credit rating (BB and below).",
  },
]

export default function Insights(props) {
  useLayoutEffect(() => {
    let scroller = () => {
      if (window.location.hash) {
        const element = document.querySelector(window.location.hash)
        if (element) {
          element.scrollIntoView({ behavior: "smooth" })
        }
      } else {
        window.scrollTo({ top: 0 })
      }
    }
    setTimeout(() => {
      scroller()
    }, 500)
  }, [])
  return (
    <div className="Insights">
      <Helmet>
        <title>WJP Rule of Law Index | Insights</title>
        <meta
          name="description"
          content={`Understand the ${latestYear} WJP Rule of Law Index insights on civic space, authoritarianism, access to justice & economic prosperity in 143 countries.`}
        />
      </Helmet>
      <InsightsHeader
        title={
          <strong>
            The rule of law has declined globally
            <br /> for the 9th year in a row.
          </strong>
        }
        backgroundImage={insightsBackground}
        links={insights.filter((item) => item.showInNav !== false)}
        color="#fff"
      />
      <div className="InsightsContainer">
        {insights.map((item, index) => {
          return <Insight key={index} {...item} />
        })}
      </div>
      <div
        className="cta"
        style={{ backgroundImage: `url(${insightsBackground})` }}
      >
        <div className="InsightsContainer">
          <div>Understand economic benefits of the rule of law.</div>
          <a
            className="button"
            target="_blank"
            rel="noopener noreferrer"
            href="https://worldjusticeproject.org/news/economic-benefits-rule-of-law"
          >
            Read the analysis
          </a>
        </div>
      </div>
    </div>
  )
}
